import { InvoiceItem } from '@ekt-group/general-purpose-api-interfaces';
import { ChevronDown, ChevronRight, Wallet03 } from '@untitled-ui/icons-react';
import { notification, TableProps } from 'antd';
import dayjs from 'dayjs';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/ui/Button';
import { DATE_FORMAT } from '@/constants';
import { useCreatePayment } from '@/features/invoices/api/use-create-payment';
import DownloadInvoiceDetailsExcelButton from '@/features/invoices/components/DownloadInvoiceDetailsExcelButton';
import { formatAsCurrency } from '@/helpers/number-formatter';
import { useBreakpoint } from '@/hooks/use-breakpoints';
import { cn } from '@/utils/cn';

import DownloadInvoiceButton from '../components/DownloadInvoiceButton';
import DownloadInvoiceDetailsButton from '../components/DownloadInvoiceDetailsButton';

type ColumnsType<T> = TableProps<T>['columns'];

type Props = {
  expandedRowKeys: string[];
  handleExpand: (key: string, event: MouseEvent, expandable: boolean) => void;
};

export function useInvoicesTable({ expandedRowKeys, handleExpand }: Props) {
  const { t } = useTranslation('invoicesPage');

  const createPaymentMutation = useCreatePayment({
    mutationConfig: {
      onSuccess: ({ data: { paymentLink } }) => {
        window.location.href = paymentLink;
      },
    },
  });

  const { isAboveMd, isBelowMd } = useBreakpoint('md');
  const { isAboveLg, isBelowLg } = useBreakpoint('lg');

  const columns: ColumnsType<InvoiceItem> = [
    {
      key: '1',
      title: t('invoiceNumber'),
      dataIndex: 'invoiceNo',
      hidden: isBelowLg,
    },
    {
      key: 'invoice',
      title: t('invoice'),
      dataIndex: 'invoiceNo',
      hidden: isAboveLg,
      render: (invoiceNo, record) => (
        <div className="flex flex-col">
          <span>{dayjs(record.invoiceDate).format(DATE_FORMAT)}</span>
          <span>{invoiceNo}</span>
        </div>
      ),
    },
    {
      key: '2',
      title: t('contractNumber'),
      dataIndex: 'contractNumber',
      hidden: isBelowMd,
    },
    {
      key: '3',
      title: t('invoiceDate'),
      dataIndex: 'invoiceDate',
      render: (value) => <span>{dayjs(value).format(DATE_FORMAT)}</span>,
      hidden: isBelowLg,
    },
    {
      key: '4',
      title: t('paymentTerm'),
      dataIndex: 'invoiceDueDate',
      render: (value) => <span>{dayjs(value).format(DATE_FORMAT)}</span>,
      hidden: isBelowLg,
    },
    {
      key: '5',
      title: t('invoiceAmount'),
      dataIndex: 'invoiceTotal',
      render: (value) => <span>{formatAsCurrency(value)}</span>,
      responsive: ['lg', 'md', 'sm'],
    },
    {
      key: '6',
      title: t('toPay'),
      dataIndex: ['isPaid', 'invoiceTotal'],
      align: 'center',
      render: (value, record) =>
        record.isPaid ? (
          <>{t('paid')}</>
        ) : (
          <div className="inline-flex items-start flex-col gap-1">
            <span className="lg:hidden inline">{dayjs(record.invoiceDueDate).format(DATE_FORMAT)}</span>
            <Button
              className="w-[155px]"
              disabled={createPaymentMutation.isPending}
              pending={createPaymentMutation.isPending}
              onClick={() => handlePayButtonClick(record.invoiceNo)}
            >
              <Wallet03 />
              <span className="h-auto py-2 font-medium text-base text-ellipsis overflow-hidden">
                {t('pay')} {formatAsCurrency(record.toPay)}
              </span>
            </Button>
          </div>
        ),
      responsive: ['lg', 'md', 'sm'],
    },
    {
      key: '7',
      title: t('invoice'),
      dataIndex: 'invoiceNo',
      align: 'center',
      render: (invoiceNo, invoice) => (invoice.isNotCompleted ? <span>N/A</span> : <DownloadInvoiceButton invoiceNo={invoiceNo} />),
      hidden: isBelowMd,
    },
    {
      key: '8',
      title: t('details'),
      dataIndex: 'invoiceNo',
      render: (invoiceNo, invoice) =>
        invoice.isNotCompleted ? (
          <span>N/A</span>
        ) : (
          <div className="flex gap-2">
            <DownloadInvoiceDetailsButton invoiceNo={invoiceNo} />
            <DownloadInvoiceDetailsExcelButton invoiceNo={invoiceNo} />
          </div>
        ),
      hidden: isBelowMd,
    },
    {
      key: 'expand',
      title: 'Details',
      width: 48,
      render: (_, record) => (
        <Button
          onClick={(event) => handleExpand(record.invoiceNo, event, !isAboveMd)}
          className={cn('h-9 w-9 ml-auto flex flex-col items-center justify-center', {
            'bg-brand-green-hover': expandedRowKeys.includes(record.invoiceNo),
          })}
          variant="primary"
        >
          {expandedRowKeys.includes(record.invoiceNo) ? <ChevronDown width={16} height={16} /> : <ChevronRight width={16} height={16} />}
        </Button>
      ),
      hidden: isAboveMd,
    },
  ];

  const handlePayButtonClick = (invoiceNo: string) => {
    createPaymentMutation.mutate(
      { invoiceNo },
      {
        onError: () => {
          notification.error({
            message: t('somethingWentWrong', { ns: 'common' }),
          });
        },
      },
    );
  };

  const expandedRowRender = (record: InvoiceItem) => {
    return (
      <>
        <div className="sm:flex hidden gap-24 p-4 bg-brand-green-light">
          <div className="flex flex-col">
            <span className="font-medium">{t('contractNumber')}</span>
            <span>{record.contractNumber}</span>
          </div>
          <div className="flex flex-col">
            <span className="font-medium">{t('invoice')}</span>
            <DownloadInvoiceButton invoiceNo={record.invoiceNo} />
          </div>
          <div className="flex flex-col">
            <span className="font-medium">{t('details')}</span>
            <div className="flex gap-2">
              <DownloadInvoiceDetailsButton invoiceNo={record.invoiceNo} />
              <DownloadInvoiceDetailsExcelButton invoiceNo={record.invoiceNo} />
            </div>
          </div>
        </div>
        <div className="sm:hidden flex flex-col divide-y divide-[#DFDFDF] justify-between bg-brand-green-light px-4 pb-4">
          <div key="contractNumber" className="flex justify-between items-center py-4">
            <span className="font-semibold">{t('contractNumber')}</span>
            <span className="text-sm">{record.contractNumber}</span>
          </div>
          <div key="paymentTerm" className="flex sm:hidden justify-between items-center py-4">
            <div className="flex flex-col">
              <span>{dayjs(record.invoiceDueDate).format(DATE_FORMAT)}</span>
              <span className="font-semibold">{t('paymentTerm')}</span>
            </div>
            {record.isPaid ? (
              <span>{t('paid')}</span>
            ) : (
              <Button
                pending={createPaymentMutation.isPending}
                disabled={createPaymentMutation.isPending}
                onClick={() => handlePayButtonClick(record.invoiceNo)}
              >
                <Wallet03 />
                <span className="h-auto py-2 font-medium text-base text-ellipsis overflow-hidden">
                  {t('pay')} {formatAsCurrency(record.toPay)}
                </span>
              </Button>
            )}
          </div>
          <div key="invoiceTotal" className="sm:hidden flex justify-between items-center py-4">
            <span className="font-semibold">{t('invoiceAmount')}</span>
            <span className="text-sm">{formatAsCurrency(record.invoiceTotal)}</span>
          </div>
          <div key="downloadInvoice" className="flex justify-between items-center py-4">
            <span className="font-semibold">{t('invoice')}</span>
            <DownloadInvoiceButton invoiceNo={record.invoiceNo} />
          </div>
          <div key="downloadInvoiceDetails" className="flex w-full justify-between items-center pt-4">
            <span className="font-semibold">{t('details')}</span>
            <div className="flex gap-2">
              <DownloadInvoiceDetailsButton invoiceNo={record.invoiceNo} />
              <DownloadInvoiceDetailsExcelButton invoiceNo={record.invoiceNo} />
            </div>
          </div>
        </div>
      </>
    );
  };

  return {
    columns,
    expandedRowRender,
  };
}
